import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar, LoadingSpinner, ErrorState, NoApplicationsState } from '../components';
import { applicationService, eventService, ticketService } from '../services';
import gatheringService from '../services/gatheringService';
import { useUser, useAuth } from '../context';
import ringArt from '../assets/img/portal-ring.png';
import { tintFor } from '../utils/tints';
import { realPlace } from '../utils/placeLabel';
import { storedLocation } from '../data/routeData';
import { recordExternalInterest } from '../utils/externalInterest';
import useTopBarHeight from '../hooks/useTopBarHeight';
import { PageHero, StatPill, SectionHead, Pill, Btn, usePageToast } from '../components/pagesystem/PageSystem';
import FindYourPeople from '../components/guest/FindYourPeople';
import {
  buildRows, groupUpcoming, pastRows, chipFor, pastChip, savedChip, metaFor, pastMeta, placeOf,
  daysTag, dollars, whenLine, goingLine, extractInviteCode,
} from '../components/guest/guestDashboardModel';
import '../components/guest/guestDashboard.css';

/**
 * /guest — FIND YOUR PARTY. (handoff: design_handoff_guest_start_calendar/Guest.dc.html).
 *
 * The hero carries the invite field and three stat pills; YOUR EVENTS is the next-up card
 * plus grouped rows (NEEDS YOU · INTERESTED · COMING UP, PAST, SAVED); YOUR PORTAL PAGE
 * is the sticky rail; FIND YOUR PEOPLE runs full width. Every list reads the same calls the
 * old page made — my applications, my invitations, my tickets, and bookmarks the first time
 * SAVED opens. Words and grouping come from components/guest/guestDashboardModel.
 */
const cityOf = (label) => (label ? label.split(',')[0].trim() : '');
const eventPath = (event) => `/events/${event?.inviteCode || event?.id}`;
const mapsUrl = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

const PinGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M12 21s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z" /><circle cx="12" cy="9" r="2.5" /></svg>
);
const LinkGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></svg>
);

function Thumb({ event }) {
  const d = event?.dateTime ? new Date(event.dateTime) : null;
  const img = event?.cardImageUrl || event?.posterUrl;
  return (
    <span className="gd-thumb">
      {img ? <img src={img} alt="" /> : d && (
        <>
          <small>{d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</small>
          <b>{d.getDate()}</b>
        </>
      )}
    </span>
  );
}

function Row({ row, chip, meta, metaDark, onOpen, alt, onAlt, act, onAct, actGrey = false, testId }) {
  const ev = row.event || {};
  return (
    <div className="gd-row" data-testid={testId}>
      <button type="button" className="gd-row-main" onClick={onOpen}>
        <Thumb event={ev} />
        <span className="gd-row-text">
          <span className="gd-row-when">
            <span className="gd-when">{whenLine(ev.dateTime)}</span>
            {chip && <span className="gd-chip" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>}
          </span>
          <span className="gd-row-title">{ev.title || 'Event'}</span>
          {meta && <span className="gd-row-meta" data-dark={metaDark ? '' : undefined}>{meta}</span>}
        </span>
      </button>
      {(alt || act) && (
        <span className="gd-row-actions">
          {alt && <Btn grey onClick={onAlt}>{alt}</Btn>}
          {act && <Btn grey={actGrey} onClick={onAct}>{act}</Btn>}
        </span>
      )}
    </div>
  );
}

function Group({ label, n, hint, hot = false, children }) {
  return (
    <div className="gd-group">
      <span className="gd-group-label">
        {hot && <i aria-hidden />}
        {label}<span>{n}</span>{hint && <small>{hint}</small>}
      </span>
      <div className="gd-card">{children}</div>
    </div>
  );
}

export default function GuestDashboardPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();
  const [say, toast] = usePageToast();
  const topH = useTopBarHeight();

  const [inviteCode, setInviteCode] = useState('');
  const [applyError, setApplyError] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [shake, setShake] = useState(0);

  const [applications, setApplications] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [gatheringInvites, setGatheringInvites] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksLoaded, setBookmarksLoaded] = useState(false);
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('upcoming');

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [appsResult, invitedResult, ticketsResult, gatheringResult] = await Promise.all([
        applicationService.getMyApplications(),
        eventService.getMyInvitedEvents(),
        ticketService.getMyTickets().catch(() => ({ data: { tickets: [] } })),
        gatheringService.myInvites().catch(() => ({ success: false })),
      ]);
      if (gatheringResult?.success) setGatheringInvites(gatheringResult.data || []);
      if (appsResult?.success) setApplications(appsResult.data || []);
      else setError(appsResult?.error || 'Failed to load applications');
      if (invitedResult?.success) setInvitations(invitedResult.data || []);
      setTickets(ticketsResult?.data?.tickets || []);
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  // SAVED lazy-loads bookmarks the first time it opens.
  useEffect(() => {
    if (tab !== 'saved' || bookmarksLoaded) return;
    let cancelled = false;
    setIsLoadingBookmarks(true);
    eventService.getMyBookmarks().then((r) => {
      if (cancelled) return;
      if (r?.success) setBookmarks(r.data || []);
    }).catch(() => {}).finally(() => {
      if (cancelled) return;
      setIsLoadingBookmarks(false);
      setBookmarksLoaded(true);
    });
    return () => { cancelled = true; };
  }, [tab, bookmarksLoaded]);

  const rows = useMemo(() => buildRows({ applications, invitations, tickets }), [applications, invitations, tickets]);
  const upcoming = useMemo(() => groupUpcoming(rows), [rows]);
  const past = useMemo(() => pastRows(rows), [rows]);

  const handleApply = async () => {
    if (!inviteCode.trim() || isApplying) return;
    setIsApplying(true);
    setApplyError(null);
    try {
      const code = extractInviteCode(inviteCode);
      const eventResult = await eventService.getEventByCode(code);
      if (!eventResult?.success) {
        setApplyError((eventResult?.error || 'Invalid invite code').toUpperCase());
        setShake((n) => n + 1);
        return;
      }
      navigate(`/events/${code}`);
    } catch {
      setApplyError('COULD NOT FIND THAT EVENT. CHECK THE CODE AND TRY AGAIN.');
      setShake((n) => n + 1);
    } finally {
      setIsApplying(false);
    }
  };

  const pass = async (row) => {
    const r = await eventService.passInvitation(row.event.id);
    if (!r?.success) { say(r?.error || 'COULD NOT PASS ON THAT ONE.'); return; }
    setInvitations((prev) => prev.filter((inv) => inv.event?.id !== row.event.id));
    say("PASSED. THEY WON'T BE TOLD.");
  };

  const unsave = async (evt) => {
    const r = await eventService.toggleBookmark(evt.id);
    if (!r?.success) { say(r?.error || 'COULD NOT UNSAVE THAT.'); return; }
    setBookmarks((prev) => prev.filter((b) => b.id !== evt.id));
    say('REMOVED FROM SAVED');
  };

  const openExternal = (row) => {
    const url = row.event?.externalTicketUrl;
    if (!url) { navigate(eventPath(row.event)); return; }
    recordExternalInterest(row.event.id);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const showTicket = (row) => {
    const t = row.tickets?.[0];
    navigate(t?.ticketCode ? `/ticket/${t.ticketCode}` : '/my-tickets');
  };

  const handleLogout = () => { logout(); navigate('/'); };

  // Portal stats — computed server-side on the profile (ProfileService.PopulateStatsAsync).
  const hostedCount = profile?.eventsHostedCount || 0;
  const attendedCount = profile?.eventsAttendedCount || 0;
  const friendsCount = profile?.friendsCount || 0;
  const city = cityOf(realPlace(storedLocation().label, profile?.location));
  const meMeta = [profile?.handle ? `@${profile.handle}` : '', city].filter(Boolean).join(' · ').toUpperCase();

  const goOk = !!inviteCode.trim() && !isApplying;
  const next = upcoming.nextUp;
  const nextEv = next?.event;
  const ticketCount = next ? (next.ticketCount || next.application?.quantity || 1) : 0;

  const listEmpty = tab === 'upcoming' ? upcoming.count === 0 && gatheringInvites.length === 0 : tab === 'past' ? past.length === 0 : bookmarks.length === 0;

  return (
    <div className="ps-page" style={{ '--ps-top': `${topH}px`, '--gd-rail-top': `${topH + 24}px` }}>
      <Navbar user={profile} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/')} />

      <PageHero
        kicker={`GUEST${city ? ` · ${city.toUpperCase()}` : ''}`}
        title="FIND YOUR PARTY."
        sub="Where you belong."
        right={(
          <div className="ps-stats">
            <StatPill n={upcoming.needs.length} label="NEED YOU" dark live={upcoming.needs.length > 0} onClick={() => setTab('upcoming')} ariaLabel={`${upcoming.needs.length} need you — show upcoming`} />
            <StatPill n={tickets.length} label={tickets.length === 1 ? 'TICKET' : 'TICKETS'} onClick={() => navigate('/my-tickets')} />
            <StatPill n={bookmarksLoaded ? bookmarks.length : undefined} label="SAVED" onClick={() => setTab('saved')} />
          </div>
        )}
      >
        <div className="gd-invite">
          <div className="gd-invite-pill" key={shake} data-shake={shake ? '' : undefined}>
            <LinkGlyph />
            <input
              value={inviteCode}
              onChange={(e) => { setInviteCode(e.target.value); setApplyError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
              placeholder="Got an invite? Paste the link or code…"
              aria-label="Invite link or code"
              autoComplete="off"
            />
            <button type="button" className="gd-invite-go" disabled={!goOk} data-busy={isApplying ? '' : undefined} onClick={handleApply}>
              {isApplying ? 'SEARCHING…' : 'GO →'}
            </button>
          </div>
          {applyError && <span className="gd-invite-err" role="alert">{applyError}</span>}
        </div>
      </PageHero>

      <main className="ps-container ps-main">
        <div className="gd-grid">
          <div className="gd-list">
            <SectionHead title="YOUR EVENTS">
              <div className="ps-pills" role="tablist" aria-label="Your events">
                {[['upcoming', 'UPCOMING', upcoming.count], ['past', 'PAST', past.length], ['saved', 'SAVED', bookmarksLoaded ? bookmarks.length : undefined]].map(([k, label, n]) => (
                  <Pill key={k} role="tab" on={tab === k} ariaSelected={tab === k} count={n} onClick={() => setTab(k)}>{label}</Pill>
                ))}
              </div>
            </SectionHead>

            {(tab !== 'saved' && isLoading) || (tab === 'saved' && isLoadingBookmarks) ? (
              <div className="gd-empty"><LoadingSpinner label="Loading events..." /></div>
            ) : tab !== 'saved' && error ? (
              <div className="gd-empty"><ErrorState message={error} onRetry={fetchDashboardData} /></div>
            ) : (
              <>
                {tab === 'upcoming' && next && (
                  <article className="gd-next" data-testid="next-up">
                    <button type="button" className="gd-next-cover" onClick={() => navigate(eventPath(nextEv))} aria-label={nextEv.title}>
                      {(nextEv.cardImageUrl || nextEv.posterUrl)
                        ? <img src={nextEv.cardImageUrl || nextEv.posterUrl} alt="" />
                        : <b style={{ background: tintFor(nextEv.id) }}>{(nextEv.title || '?')[0]}</b>}
                      <span className="gd-badge">YOU'RE IN</span>
                    </button>
                    <div className="gd-next-body">
                      <div className="gd-next-top">
                        <span className="gd-title-stack">
                          <span className="gd-when">{whenLine(nextEv.dateTime)}</span>
                          <button type="button" className="gd-next-title" onClick={() => navigate(eventPath(nextEv))}>{nextEv.title}</button>
                          {nextEv.hostName && <span className="gd-host">HOSTED BY {nextEv.hostName.toUpperCase()}</span>}
                        </span>
                        <span className="gd-clock"><b>{daysTag(nextEv.dateTime)}</b><span>TO EVENT</span></span>
                      </div>
                      {next.application?.locationAddress && (
                        <div className="gd-loc">
                          <PinGlyph />
                          <span className="gd-loc-text"><b>LOCATION REVEALED</b><span>{next.application.locationAddress}</span></span>
                          <a href={mapsUrl(next.application.locationAddress)} target="_blank" rel="noopener noreferrer">MAP →</a>
                        </div>
                      )}
                      <div className="gd-foot">
                        <span className="gd-going">
                          {(nextEv.faces || []).length > 0 && (
                            <span className="gd-faces">
                              {nextEv.faces.slice(0, 3).map((f, i) => (
                                <span key={f.userId || i} style={{ background: tintFor(f.userId || f.displayName) }} title={f.displayName}>
                                  {f.avatarUrl ? <img src={f.avatarUrl} alt="" referrerPolicy="no-referrer" /> : (f.displayName || '?')[0]}
                                </span>
                              ))}
                            </span>
                          )}
                          <span>{goingLine(nextEv)}</span>
                        </span>
                        <span className="gd-foot-actions">
                          <Btn grey lg onClick={() => navigate(eventPath(nextEv))}>EVENT →</Btn>
                          <Btn lg onClick={() => showTicket(next)}>SHOW TICKET<small>{ticketCount}</small></Btn>
                        </span>
                      </div>
                    </div>
                  </article>
                )}

                {tab === 'upcoming' && gatheringInvites.length > 0 && (
                  <Group label="GATHERINGS" n={gatheringInvites.length} hint="· YOU'RE INVITED" hot>
                    {gatheringInvites.map((gi) => {
                      const open = () => navigate(`/gatherings/${gi.gatheringId}`);
                      const answered = gi.viewerRsvp === 'going' || gi.viewerRsvp === 'maybe';
                      return (
                        <Row
                          key={gi.gatheringId}
                          row={{ event: { title: gi.name, dateTime: gi.startsAt, cardImageUrl: gi.posterUrl } }}
                          chip={answered ? { label: gi.viewerRsvp === 'going' ? 'GOING' : 'MAYBE', bg: '#22C55E', fg: '#fff' } : chipFor({ status: 'invited' })}
                          meta={[`FROM ${(gi.invitedByName || 'SOMEONE').toUpperCase()}`, gi.eventCount ? `${gi.eventCount} ${gi.eventCount === 1 ? 'EVENT' : 'EVENTS'}` : ''].filter(Boolean).join(' · ')}
                          onOpen={open}
                          act={answered ? 'GATHERING →' : 'RSVP →'}
                          actGrey={answered}
                          onAct={open}
                          testId="row-gathering-invite"
                        />
                      );
                    })}
                  </Group>
                )}

                {tab === 'upcoming' && upcoming.needs.length > 0 && (
                  <Group label="NEEDS YOU" n={upcoming.needs.length} hot>
                    {upcoming.needs.map((row) => {
                      const m = metaFor(row);
                      if (row.status === 'invited') {
                        return <Row key={row.key} row={row} chip={chipFor(row)} meta={m.text} onOpen={() => navigate(eventPath(row.event))} alt="PASS" onAlt={() => pass(row)} act="APPLY →" onAct={() => navigate(eventPath(row.event))} testId="row-invited" />;
                      }
                      const due = row.application?.amountDueCents || 0;
                      return <Row key={row.key} row={row} chip={chipFor(row)} meta={m.text} metaDark onOpen={() => navigate(eventPath(row.event))} act={row.status === 'payment_failed' ? 'RETRY →' : (due ? `PAY ${dollars(due)}` : 'PAY →')} onAct={() => navigate(eventPath(row.event))} testId="row-pay" />;
                    })}
                  </Group>
                )}

                {tab === 'upcoming' && upcoming.interested.length > 0 && (
                  <Group label="INTERESTED" n={upcoming.interested.length} hint="· OFF-PORTAL EVENTS YOU FLAGGED">
                    {upcoming.interested.map((row) => (
                      <Row key={row.key} row={row} chip={chipFor(row)} meta={metaFor(row).text} onOpen={() => navigate(eventPath(row.event))} act="TICKETS ↗" actGrey onAct={() => openExternal(row)} testId="row-interested" />
                    ))}
                  </Group>
                )}

                {tab === 'upcoming' && upcoming.comingUp.length > 0 && (
                  <Group label="COMING UP" n={upcoming.comingUp.length}>
                    {upcoming.comingUp.map((row) => (
                      <Row
                        key={row.key}
                        row={row}
                        chip={chipFor(row)}
                        meta={metaFor(row).text}
                        onOpen={() => (row.kind === 'ticket' ? showTicket(row) : navigate(eventPath(row.event)))}
                        act={row.kind === 'ticket' || row.ticketCount > 0 ? 'SHOW TICKET' : 'EVENT →'}
                        actGrey
                        onAct={() => (row.kind === 'ticket' || row.ticketCount > 0 ? showTicket(row) : navigate(eventPath(row.event)))}
                        testId="row-coming"
                      />
                    ))}
                  </Group>
                )}

                {tab === 'past' && past.length > 0 && (
                  <Group label="PAST" n={past.length}>
                    {past.map((row) => (
                      <Row key={row.key} row={row} chip={pastChip(row)} meta={pastMeta(row)} onOpen={() => navigate(eventPath(row.event))} act="RECAP →" actGrey onAct={() => navigate(eventPath(row.event))} testId="row-past" />
                    ))}
                  </Group>
                )}

                {tab === 'saved' && bookmarks.length > 0 && (
                  <Group label="SAVED" n={bookmarks.length}>
                    {bookmarks.map((evt) => {
                      const pledge = evt.fundingType === 'party_starter' || evt.status === 'funding';
                      const row = { event: evt };
                      return (
                        <Row
                          key={evt.id}
                          row={row}
                          chip={savedChip(evt.status)}
                          meta={[placeOf(evt), evt.maxAttendees ? `${evt.acceptedCount || 0}/${evt.maxAttendees} GOING` : ''].filter(Boolean).join(' · ')}
                          onOpen={() => navigate(eventPath(evt))}
                          alt="UNSAVE"
                          onAlt={() => unsave(evt)}
                          act={pledge ? 'PLEDGE →' : 'GET IN →'}
                          onAct={() => navigate(eventPath(evt))}
                          testId="row-saved"
                        />
                      );
                    })}
                  </Group>
                )}

                {listEmpty && (
                  tab === 'saved' ? (
                    <div className="gd-empty">
                      <b>NO SAVED EVENTS.</b>
                      <span>Bookmark events you're interested in to see them here.</span>
                      <Btn lg onClick={() => navigate('/featured')}>BROWSE EVENTS →</Btn>
                    </div>
                  ) : tab === 'past' ? (
                    <div className="gd-empty">
                      <b>NO PAST EVENTS</b>
                      <span>Events you've attended will show up here</span>
                    </div>
                  ) : (
                    <div className="gd-empty" style={{ padding: 0 }}><NoApplicationsState type="guest" /></div>
                  )
                )}
              </>
            )}
          </div>

          <aside className="gd-side">
            <div className="gd-panel">
              <h2>YOUR PORTAL PAGE</h2>
              <div className="gd-me">
                <button type="button" className="gd-ava" onClick={() => navigate(`/users/${profile?.userId}`)} aria-label="View your Portal Page">
                  <i style={{ backgroundImage: `url(${ringArt})` }} aria-hidden />
                  <span style={{ background: tintFor(profile?.userId || profile?.displayName) }}>
                    {profile?.avatarUrl || profile?.faceUrl ? <img src={profile.avatarUrl || profile.faceUrl} alt="" referrerPolicy="no-referrer" /> : (profile?.displayName || '?')[0]}
                  </span>
                </button>
                <span className="gd-me-text">
                  <b>{(profile?.displayName || 'YOUR NAME').toUpperCase()}</b>
                  {meMeta && <span>{meMeta}</span>}
                </span>
              </div>
              <div className="gd-tiles">
                <button type="button" className="gd-tile" onClick={() => navigate('/host')}><b>{hostedCount}</b><span>HOSTED</span></button>
                <button type="button" className="gd-tile" onClick={() => navigate(`/users/${profile?.userId}`)}><b>{attendedCount}</b><span>ATTENDED</span></button>
                <button type="button" className="gd-tile" onClick={() => navigate('/friends')}><b>{friendsCount}</b><span>FRIENDS</span></button>
              </div>
              <div className="gd-two">
                <Btn grey lg onClick={() => navigate(`/users/${profile?.userId}`)}>VIEW</Btn>
                <Btn lg onClick={() => navigate('/portal/edit')}>EDIT</Btn>
              </div>
            </div>
          </aside>
        </div>

        <FindYourPeople limit={6} onSay={say} />
      </main>
      {toast}
    </div>
  );
}
